import { defineStore } from 'pinia'
import { toast } from 'vue-sonner'
import router from '@/router'
import { onboardingApi as api } from '@/api/endpoints/onboarding'
import { readFresh } from '@/queries/readFresh'
import { useAuthStore } from './auth.store'
import { useLoaderStore } from './loader.store'
import {
  AUDIENCE_OPTIONS,
  BUSINESS_STAGE_OPTIONS,
  CCO_EMPLOYMENT_OPTIONS,
  COMPANY_ROLE_OPTIONS,
  ENTITY_TYPE_OPTIONS,
  FINANCIAL_PRODUCT_OPTIONS,
  LICENSE_TYPE_OPTIONS,
  ONBOARDING_GOOGLE_DRIVE_ENABLED,
  ONBOARDING_STEPS,
  OPERATING_REGION_OPTIONS,
  SETUP_DOC_FIELD_MAP,
  parseYesNo,
  resolveOptionLabel,
  resolveOptionLabels,
  resolveOptionValues,
  yesNo,
  yesNoLower,
} from '@/constants/onboarding'

function hasValue(value) {
  if (value === null || value === undefined || value === '') return false
  if (Array.isArray(value)) return value.length > 0
  if (value instanceof File) return true
  if (typeof value === 'object') {
    return !!(value.full_address || value.fullAddress)
  }
  return true
}

function isValidEmail(value) {
  if (!hasValue(value)) return false
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())
}

function createInitialState() {
  return {
    submitting: false,
    setupSubmitted: false,
    /** True after Dashboard / Invite exit — blocks returning via browser Back. */
    exited: false,
    workspaceId: null,
    description: '',
    stepStatuses: {},
    /** Cached master-questions for business step (not persisted). */
    masterQuestions: [],
    /** Persisted answers keyed by question for business step. */
    masterAnswers: [],
    company: {
      docs: { formation: null, tax: null, license: null },
      legalName: '',
      hasDba: false,
      dbaName: '',
      country: '',
      entityType: '',
      website: '',
      physicalAddress: '',
      mailingSameAsPhysical: false,
      mailingAddress: '',
      registeredSameAsPhysical: false,
      registeredAddress: '',
      contactName: '',
      contactEmail: '',
    },
    business: {
      companyDescription: '',
      companyRoles: [],
      otherCompanyRole: '',
      financialProducts: [],
      otherProductsServices: '',
      audience: [],
      typicalCustomers: '',
      customerLocations: [],
      operatingRegions: [],
      businessStage: '',
      holdsLicenses: false,
      licenseTypes: [],
      otherLicenseText: '',
    },
    cco: {
      // null until the user explicitly chooses Yes/No — defaulting to "need"
      // made isStepFilled(3) true and painted the stepper green prematurely.
      option: null,
      form: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        employmentType: '',
        appointmentDate: '',
        invite: true,
        requestFractional: false,
      },
    },
    auditor: {
      option: null,
      form: {
        fullName: '',
        email: '',
        organization: '',
        invite: false,
        requestComplyOne: false,
      },
    },
    documents: {
      policies: { option: null, files: [] },
      training: { option: null, files: [] },
      riskAssessment: { option: null, files: [] },
    },
  }
}

function appendFile(formData, key, file) {
  if (file instanceof File) {
    formData.append(key, file)
  }
}

function appendFiles(formData, key, files) {
  files.filter(Boolean).forEach((file) => {
    formData.append(`${key}[]`, file)
  })
}

function formatAddress(address) {
  if (!address) return ''
  if (typeof address === 'object') {
    return address.full_address || address.fullAddress || ''
  }
  return String(address)
}

function applyMultiSelectAnswers(labels, options, target, valuesKey, otherKey) {
  const { values, unmatched } = resolveOptionValues(labels, options)
  if (unmatched.length && otherKey) {
    if (!values.includes('other')) values.push('other')
    const existing = target[otherKey] ? `${target[otherKey]}; ` : ''
    target[otherKey] = `${existing}${unmatched.join('; ')}`.trim()
  }
  if (values.length) target[valuesKey] = values
}

export const useOnboardingStore = defineStore('onboarding', {
  state: () => createInitialState(),

  actions: {
    getStepValidationError(stepNumber) {
      const { company, business, cco, auditor, documents } = this

      switch (stepNumber) {
        case 1: {
          if (!hasValue(company.legalName)) {
            return 'Please enter the full legal name of your organization'
          }
          if (company.hasDba && !hasValue(company.dbaName)) {
            return 'Please enter your DBA / brand / product name'
          }
          if (!hasValue(company.country)) {
            return 'Please select the country of incorporation / headquarters'
          }
          if (!hasValue(company.entityType)) {
            return 'Please select your legal entity type'
          }
          if (!hasValue(company.contactName)) {
            return 'Please enter the primary contact full name'
          }
          if (!isValidEmail(company.contactEmail)) {
            return 'Please enter a valid primary contact email'
          }
          return null
        }
        case 2: {
          if (this.masterQuestions.length) {
            return this.getMasterQuestionsValidationError()
          }
          if (!hasValue(business.companyDescription)) {
            return 'Please describe what your company does'
          }
          if (!business.financialProducts.length) {
            return 'Please select your primary business'
          }
          if (
            business.financialProducts.includes('other') &&
            !hasValue(business.otherProductsServices)
          ) {
            return 'Please specify your primary business'
          }
          if (!business.audience.length) {
            return 'Please select who you serve'
          }
          if (!business.operatingRegions.length) {
            return 'Please select where you operate'
          }
          if (!hasValue(business.businessStage)) {
            return 'Please select your business stage'
          }
          if (business.holdsLicenses && !business.licenseTypes.length) {
            return 'Please select at least one license type'
          }
          if (
            business.holdsLicenses &&
            business.licenseTypes.includes('other') &&
            !hasValue(business.otherLicenseText)
          ) {
            return 'Please specify your other license type'
          }
          return null
        }
        case 3: {
          if (!cco.option) {
            return 'Please indicate whether you have a designated CCO'
          }
          if (cco.option === 'have') {
            const ccoName = [cco.form.firstName, cco.form.lastName]
              .filter(Boolean)
              .join(' ')
              .trim()
            if (!hasValue(ccoName)) {
              return 'Please enter the CCO full name'
            }
            if (!isValidEmail(cco.form.email)) {
              return 'Please enter a valid CCO email address'
            }
            if (!hasValue(cco.form.phone)) {
              return 'Please enter the CCO phone number'
            }
            if (!hasValue(cco.form.employmentType)) {
              return 'Please select the CCO employment type'
            }
          }
          if (!auditor.option) {
            return 'Please indicate whether you have an Independent Reviewer'
          }
          if (auditor.option === 'have') {
            if (!hasValue(auditor.form.fullName)) {
              return 'Please enter the reviewer full name'
            }
            if (!isValidEmail(auditor.form.email)) {
              return 'Please enter a valid reviewer email address'
            }
            if (!hasValue(auditor.form.organization)) {
              return 'Please enter the reviewer organization / firm name'
            }
          }
          return null
        }
        case 4: {
          for (const key of ['policies', 'training', 'riskAssessment']) {
            if (documents[key].option === null) {
              return 'Please choose an option for each document section'
            }
            if (
              documents[key].option === 'have' &&
              !documents[key].files.length
            ) {
              return 'Please upload files for each document you said you have'
            }
          }
          return null
        }
        default:
          return null
      }
    },

    isStepFilled(stepNumber) {
      return this.getStepValidationError(stepNumber) === null
    },

    answerMatchesDependency(targetAnswer, expectedValue) {
      const targetValue = String(expectedValue ?? '').toLowerCase()
      if (Array.isArray(targetAnswer)) {
        return targetAnswer.some(
          (val) => String(val ?? '').toLowerCase() === targetValue,
        )
      }
      return String(targetAnswer ?? '').toLowerCase() === targetValue
    },

    isMasterQuestionVisible(question) {
      if (!question?.depends_on?.question_number) return true
      const parent = this.masterAnswers.find(
        (a) => a.question_number == question.depends_on.question_number,
      )
      if (!parent) return false
      return this.answerMatchesDependency(
        parent.answer,
        question.depends_on.value,
      )
    },

    hasMasterAnswer(answer, type) {
      if (answer === null || answer === undefined || answer === '') return false
      if (Array.isArray(answer)) return answer.length > 0
      if (type === 'address' && typeof answer === 'object') {
        return !!(answer.full_address || answer.fullAddress)
      }
      return true
    },

    getMasterQuestionsValidationError() {
      if (!hasValue(this.business.companyDescription)) {
        return 'Please describe what your company does'
      }
      for (const question of this.masterQuestions) {
        if (!question.is_required) continue
        if (!this.isMasterQuestionVisible(question)) continue
        const entry = this.masterAnswers.find(
          (a) => a.question_id === question.id,
        )
        if (!this.hasMasterAnswer(entry?.answer, question.type)) {
          return `Please answer: ${question.title}`
        }
      }
      return null
    },

    async GetMasterQuestions(params = { per_page: 150 }) {
      // Re-read on every visit (the answers are restored onto it), sharing concurrent requests.
      const body = await readFresh(['master-questions', params], () => api.masterQuestions(params))
      return body.data
    },

    /**
     * Load workspace onboarding master questions and initialize answers.
     * Preserves previously persisted answers by question_id / setup_field.
     */
    async loadBusinessMasterQuestions(processFn) {
      const data = await this.GetMasterQuestions({
        per_page: 150,
        form_template_type: 'workspace',
      })
      const questions = data?.questions ?? []
      const {
        processedQuestionnaires,
        initialAnswers,
      } = processFn(questions)

      const flatQuestions = processedQuestionnaires.flatMap(
        (section) => section.questions,
      )
      this.masterQuestions = flatQuestions

      const previousById = new Map(
        (this.masterAnswers || []).map((a) => [a.question_id, a.answer]),
      )
      const previousByField = new Map(
        (this.masterAnswers || [])
          .filter((a) => a.setup_field)
          .map((a) => [a.setup_field, a.answer]),
      )

      this.masterAnswers = initialAnswers.map((answer) => {
        const restored =
          previousById.get(answer.question_id) ??
          previousByField.get(answer.setup_field)
        if (restored === undefined) return answer
        // Clone arrays so restored multi-selects never share one reference
        const nextAnswer = Array.isArray(restored) ? [...restored] : restored
        return { ...answer, answer: nextAnswer }
      })

      this.syncMasterAnswersToBusiness()
      return { processedQuestionnaires, flatQuestions }
    },

    getSetupFieldAnswers() {
      const out = {}
      for (const entry of this.masterAnswers || []) {
        if (!entry?.setup_field) continue
        if (!this.isMasterQuestionVisible(entry)) continue
        out[entry.setup_field] = entry.answer
      }
      return out
    },

    syncMasterAnswersToBusiness() {
      const fields = this.getSetupFieldAnswers()
      const { business } = this

      if (fields.holds_licenses !== undefined && fields.holds_licenses !== '') {
        business.holdsLicenses = parseYesNo(fields.holds_licenses)
      }

      if (Array.isArray(fields.license_types)) {
        business.licenseTypes = fields.license_types
      }
      if (typeof fields.other_license_types === 'string') {
        business.otherLicenseText = fields.other_license_types
      }

      if (Array.isArray(fields.company_roles)) {
        business.companyRoles = fields.company_roles
      }
      if (typeof fields.other_company_role === 'string') {
        business.otherCompanyRole = fields.other_company_role
      }

      if (Array.isArray(fields.products_services)) {
        business.financialProducts = fields.products_services
      }
      if (typeof fields.other_products_services === 'string') {
        business.otherProductsServices = fields.other_products_services
      }

      if (Array.isArray(fields.offered_to)) {
        business.audience = fields.offered_to
      }
      if (Array.isArray(fields.customer_locations)) {
        business.customerLocations = fields.customer_locations
      }
      if (typeof fields.typical_customers === 'string') {
        business.typicalCustomers = fields.typical_customers
      }
      if (typeof fields.company_description === 'string') {
        business.companyDescription = fields.company_description
        this.description = fields.company_description
      }
    },

    setStepStatus(stepNumber, status) {
      this.stepStatuses = {
        ...this.stepStatuses,
        [stepNumber]: status,
      }
    },

    markStepOnContinue(stepNumber) {
      // Filled → green completed; left empty → orange skipped (same as "Complete later")
      this.setStepStatus(
        stepNumber,
        this.isStepFilled(stepNumber) ? 'completed' : 'skipped',
      )
    },

    getNextStep(stepNumber) {
      const currentIndex = ONBOARDING_STEPS.findIndex(
        (step) => step.number === stepNumber,
      )
      return currentIndex >= 0 ? ONBOARDING_STEPS[currentIndex + 1] ?? null : null
    },

    getFirstSkippedStep() {
      return (
        ONBOARDING_STEPS.find(
          (step) => this.stepStatuses[step.number] !== 'completed',
        ) ?? null
      )
    },

    goToComplete() {
      this.stepStatuses = this.finalizeStepStatuses()
      router.push('/onboarding/complete')
    },

    resumeSkippedSteps() {
      const firstSkipped = this.getFirstSkippedStep()
      if (!firstSkipped) {
        this.goToComplete()
        return
      }
      router.push(firstSkipped.route)
    },

    async advanceFromStep(stepNumber) {
      // Views own field-level UX (highlight + scroll). Keep this as a safety net.
      if (this.getStepValidationError(stepNumber)) {
        return false
      }

      // Mark before navigate so the route-leave guard can skip a second validate.
      this.markStepOnContinue(stepNumber)

      const nextStep = this.getNextStep(stepNumber)

      // Drive step needs a workspace, so submit first when that path is enabled.
      if (nextStep?.key === 'google-drive' && ONBOARDING_GOOGLE_DRIVE_ENABLED) {
        await this.submitSetup({ navigateToDrive: true })
        return true
      }

      if (!nextStep || nextStep.key === 'google-drive') {
        this.goToComplete()
        return true
      }

      // Prefetch chunk then navigate — avoids a blank pause on first visit.
      const route = router.resolve(nextStep.route)
      await Promise.all(
        route.matched
          .map((record) => record.components?.default)
          .filter((comp) => typeof comp === 'function')
          .map((load) => load().catch(() => null)),
      )

      await router.push(nextStep.route)
      return true
    },

    async completeLater(stepNumber) {
      this.setStepStatus(stepNumber, 'skipped')

      const nextStep = this.getNextStep(stepNumber)

      // Skip optional drive; defer API submit until "Go to My Dashboard".
      if (!nextStep || nextStep.key === 'google-drive') {
        this.goToComplete()
        return
      }

      router.push(nextStep.route)
    },

    resolveWorkspaceId() {
      return (
        this.workspaceId ??
        useAuthStore().user?.workspace_id ??
        useAuthStore().defaultWorkspaceId ??
        null
      )
    },

    /**
     * Submit workspace setup once if it has not been submitted yet.
     * Does not navigate or clear local onboarding state.
     */
    async ensureSetupSubmitted(options = {}) {
      if (this.setupSubmitted) return
      await this.submitSetup({
        navigateToComplete: false,
        navigateToDrive: false,
        ...options,
      })
    },

    /**
     * Hard-exit lock: clear drafts and mark flow finished so route guards
     * bounce browser Back away from complete / step forms.
     */
    lockAfterExit(workspaceId = null) {
      const id = workspaceId ?? this.resolveWorkspaceId()
      this.reset()
      this.exited = true
      if (id) this.workspaceId = id
      return id
    },

    /**
     * Leave the complete screen into a workspace route.
     *
     * Soft leave (invite, etc.): keep summary state so Back returns to a
     * working complete page. Hard leave (final exit): clear drafts, lock
     * the flow, and replace so history cannot revive onboarding.
     *
     * @param {string} path - Path under `/workspaces/:id` (e.g. `/users/add`)
     * @param {{ clearState?: boolean, replace?: boolean }} [options]
     */
    async leaveToWorkspace(
      path = '/dashboard',
      { clearState = true, replace = clearState } = {},
    ) {
      await this.ensureSetupSubmitted()

      const id = this.resolveWorkspaceId()
      const target = id ? `/workspaces/${id}${path}` : '/workspaces'

      if (clearState) {
        this.lockAfterExit(id)
      }

      if (replace) {
        await router.replace(target)
        return
      }
      await router.push(target)
    },

    async submitAndLeave(path = '/dashboard', options) {
      return this.leaveToWorkspace(path, options)
    },

    async submitAndGoToDashboard() {
      return this.leaveToWorkspace('/dashboard', {
        clearState: true,
        replace: true,
      })
    },

    applySetupAnswers(answers) {
      if (!answers) return

      const { company, business } = this

      if (answers.company_name) company.legalName = answers.company_name
      if (answers.has_dba !== undefined && answers.has_dba !== '') {
        company.hasDba = parseYesNo(answers.has_dba)
      }
      if (answers.dba_name) company.dbaName = answers.dba_name

      const country =
        answers.other_country_of_headquarters || answers.country_of_headquarters
      if (country) company.country = country

      if (answers.company_website) company.website = answers.company_website
      if (answers.physical_address) {
        company.physicalAddress = answers.physical_address
      }

      if (
        answers.mailing_same_as_physical !== undefined &&
        answers.mailing_same_as_physical !== ''
      ) {
        company.mailingSameAsPhysical = parseYesNo(answers.mailing_same_as_physical)
      }
      if (answers.mailing_address && !company.mailingSameAsPhysical) {
        company.mailingAddress = answers.mailing_address
      }

      if (
        answers.registered_same_as_physical !== undefined &&
        answers.registered_same_as_physical !== ''
      ) {
        company.registeredSameAsPhysical = parseYesNo(
          answers.registered_same_as_physical,
        )
      }
      if (answers.registered_address && !company.registeredSameAsPhysical) {
        company.registeredAddress = answers.registered_address
      }

      if (answers.primary_contact_email) {
        company.contactEmail = answers.primary_contact_email
      }
      if (answers.primary_contact_name) {
        company.contactName = answers.primary_contact_name
      }
      if (answers.entity_type) {
        company.entityType = answers.entity_type
      }

      if (answers.company_description) {
        business.companyDescription = answers.company_description
        this.description = answers.company_description
      }
      if (answers.business_stage) {
        business.businessStage = answers.business_stage
      }

      applyMultiSelectAnswers(
        answers.company_roles,
        COMPANY_ROLE_OPTIONS,
        business,
        'companyRoles',
        'otherCompanyRole',
      )
      applyMultiSelectAnswers(
        answers.products_services,
        FINANCIAL_PRODUCT_OPTIONS,
        business,
        'financialProducts',
        'otherProductsServices',
      )

      const audienceMatch = resolveOptionValues(answers.offered_to, AUDIENCE_OPTIONS)
      if (audienceMatch.values.length) business.audience = audienceMatch.values
      if (audienceMatch.unmatched.length) {
        const existing = business.typicalCustomers
          ? `${business.typicalCustomers}; `
          : ''
        business.typicalCustomers = `${existing}${audienceMatch.unmatched.join('; ')}`.trim()
      }

      applyMultiSelectAnswers(
        answers.operating_regions,
        OPERATING_REGION_OPTIONS,
        business,
        'operatingRegions',
      )

      if (Array.isArray(answers.customer_locations)) {
        business.customerLocations = answers.customer_locations.filter(Boolean)
      }
      if (answers.typical_customers) {
        business.typicalCustomers = answers.typical_customers
      }

      if (answers.holds_licenses !== undefined && answers.holds_licenses !== '') {
        business.holdsLicenses = parseYesNo(answers.holds_licenses)
      }
      if (business.holdsLicenses) {
        applyMultiSelectAnswers(
          answers.license_types,
          LICENSE_TYPE_OPTIONS,
          business,
          'licenseTypes',
          'otherLicenseText',
        )
        if (answers.other_license_types) {
          business.otherLicenseText = answers.other_license_types
        }
      }
    },

    async extractAnswersFromUploadedDoc() {
      const docKey = ['formation', 'tax', 'license'].find(
        (key) => this.company.docs[key] instanceof File,
      )

      if (!docKey) {
        toast.error('Please upload a document first.')
        return null
      }

      return this.extractAnswersFromFile(docKey)
    },

    async extractAnswersFromFile(docKey) {
      const fieldName = SETUP_DOC_FIELD_MAP[docKey]
      const file = this.company.docs[docKey]
      if (!fieldName || !(file instanceof File)) return null

      useLoaderStore().show('Extracting information from your document...')

      try {
        const formData = new FormData()
        formData.append(fieldName, file)

        const body = await api.answerFromFile(formData)

        const answers = body?.data?.answers
        if (answers) {
          this.applySetupAnswers(answers)
          toast.success(
            body.message || 'Company information pre-filled from document.',
          )
        }

        return answers
      } finally {
        useLoaderStore().hide()
      }
    },

    buildPayload() {
      const { company, business, cco, auditor, documents } = this
      const physicalAddress = formatAddress(company.physicalAddress)
      const mailingAddress = company.mailingSameAsPhysical
        ? physicalAddress
        : formatAddress(company.mailingAddress)
      const registeredAddress = company.registeredSameAsPhysical
        ? physicalAddress
        : formatAddress(company.registeredAddress)
      const hasCco = cco.option === 'have'
      const hasAuditor = auditor.option === 'have'
      const setupFields = this.getSetupFieldAnswers()

      return {
        // `lagel` is intentionally kept to match the backend contract.
        lagel_organization_name: company.legalName,
        has_dba: yesNoLower(company.hasDba),
        dba_name: company.hasDba ? company.dbaName : '',
        country_of_headquarters: company.country,
        legal_entity_type: resolveOptionLabel(
          company.entityType,
          ENTITY_TYPE_OPTIONS,
        ),
        registered_address: registeredAddress,
        physical_address: physicalAddress,
        mailing_address: mailingAddress,
        mailing_same_as_physical: company.mailingSameAsPhysical,
        registered_same_as_physical: company.registeredSameAsPhysical,
        company_website: company.website,
        primary_contact_name: company.contactName,
        primary_contact_email: company.contactEmail,
        description:
          business.companyDescription ||
          this.description ||
          `Workspace setup for ${company.legalName}`.trim(),
        primary_business: resolveOptionLabels(
          business.financialProducts,
          FINANCIAL_PRODUCT_OPTIONS,
        ),
        customer_types: resolveOptionLabels(
          business.audience,
          AUDIENCE_OPTIONS,
        ),
        operating_regions: resolveOptionLabels(
          business.operatingRegions,
          OPERATING_REGION_OPTIONS,
        ),
        business_stage: resolveOptionLabel(
          business.businessStage,
          BUSINESS_STAGE_OPTIONS,
        ),
        holds_licenses:
          setupFields.holds_licenses ?? yesNo(business.holdsLicenses),
        license_types:
          setupFields.license_types ??
          (business.holdsLicenses
            ? resolveOptionLabels(business.licenseTypes, LICENSE_TYPE_OPTIONS)
            : []),
        other_license_types:
          setupFields.other_license_types ??
          (business.holdsLicenses && business.otherLicenseText
            ? business.otherLicenseText
            : null),
        company_roles: setupFields.company_roles ?? business.companyRoles ?? [],
        other_company_role:
          setupFields.other_company_role ?? business.otherCompanyRole ?? null,
        products_services:
          setupFields.products_services ?? business.financialProducts ?? [],
        other_products_services:
          setupFields.other_products_services ??
          business.otherProductsServices ??
          null,
        offered_to: setupFields.offered_to ?? business.audience ?? [],
        typical_customers:
          setupFields.typical_customers ?? business.typicalCustomers ?? null,
        customer_locations:
          setupFields.customer_locations ?? business.customerLocations ?? [],
        has_cco: yesNoLower(hasCco),
        cco_full_name: hasCco
          ? [cco.form.firstName, cco.form.lastName].filter(Boolean).join(' ')
          : '',
        cco_email: hasCco ? cco.form.email : '',
        cco_phone: hasCco ? cco.form.phone : '',
        cco_employment_type: hasCco
          ? resolveOptionLabel(
              cco.form.employmentType,
              CCO_EMPLOYMENT_OPTIONS,
            )
          : '',
        cco_invite: hasCco ? yesNoLower(cco.form.invite) : 'no',
        assign_cco: hasCco ? 'yes' : 'no',
        request_fractional_cco: hasCco
          ? 'no'
          : yesNoLower(cco.form.requestFractional),
        has_auditor: yesNoLower(hasAuditor),
        auditor_name: hasAuditor ? auditor.form.fullName : '',
        auditor_email: hasAuditor ? auditor.form.email : '',
        auditor_organization: hasAuditor ? auditor.form.organization : '',
        auditor_invite: hasAuditor ? yesNoLower(auditor.form.invite) : 'no',
        request_independent_reviewer: hasAuditor
          ? 'no'
          : yesNoLower(auditor.form.requestComplyOne),
        fill_questionnaire: true,
        company_formation_document: company.docs.formation,
        tax_document: company.docs.tax,
        license_document: company.docs.license,
        policy_files:
          documents.policies.option === 'have' ? documents.policies.files : [],
        training_files:
          documents.training.option === 'have' ? documents.training.files : [],
        risk_assessment_files:
          documents.riskAssessment.option === 'have'
            ? documents.riskAssessment.files
            : [],
      }
    },

    buildFormData() {
      const payload = this.buildPayload()
      const formData = new FormData()

      Object.entries(payload).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          appendFiles(formData, key, value)
          return
        }
        if (value instanceof File) {
          appendFile(formData, key, value)
          return
        }
        formData.append(key, value ?? '')
      })

      return formData
    },

    async submitSetup({
      navigateToDrive = false,
      navigateToComplete = true,
      silent = false,
    } = {}) {
      if (this.submitting) return
      if (this.setupSubmitted) {
        if (navigateToComplete) this.goToComplete()
        return
      }

      this.submitting = true
      if (!silent) {
        useLoaderStore().show('Setting up your workspace...')
      }

      try {
        const payload = this.buildFormData()
        const body = await api.setupWorkspace(payload)

        const { workspace, questionnaire_ai_fill } = body.data ?? {}
        if (!silent) {
          toast.success(body.message)
        }

        const authStore = useAuthStore()
        await authStore.GetProfile(undefined, { silent })

        const hadWorkspace = !!authStore.user?.workspace_id
        const activeWorkspace = authStore.user.default_workspace ?? workspace
        if (activeWorkspace?.id) {
          authStore.ApplyDefaultWorkspace(activeWorkspace)
        }

        if (!hadWorkspace && authStore.user?.workspace_id) {
          authStore.syncAfterWorkspaceSetup()
        }

        if (questionnaire_ai_fill?.message && !silent) {
          toast.info(questionnaire_ai_fill.message)
        }

        const workspaceId = authStore.user.workspace_id ?? workspace?.id
        this.workspaceId = workspaceId
        this.setupSubmitted = true

        if (
          navigateToDrive &&
          ONBOARDING_GOOGLE_DRIVE_ENABLED &&
          workspaceId
        ) {
          const stepStatuses = { ...this.stepStatuses }
          this.reset()
          this.workspaceId = workspaceId
          this.stepStatuses = stepStatuses
          this.setupSubmitted = true
          router.push('/onboarding/google-drive')
          return body.data
        }

        if (navigateToComplete) {
          this.finishOnboarding(workspaceId)
        }
        return body.data
      } finally {
        this.submitting = false
        if (!silent) {
          useLoaderStore().hide()
        }
      }
    },

    finalizeStepStatuses() {
      const statuses = { ...this.stepStatuses }

      ONBOARDING_STEPS.forEach((step) => {
        if (statuses[step.number] === 'completed' || statuses[step.number] === 'skipped') {
          return
        }

        statuses[step.number] = this.isStepFilled(step.number)
          ? 'completed'
          : 'skipped'
      })

      return statuses
    },

    finishOnboarding(workspaceId = this.workspaceId) {
      const stepStatuses = this.finalizeStepStatuses()
      const resolvedWorkspaceId =
        workspaceId ?? this.workspaceId ?? useAuthStore().user?.workspace_id ?? null
      const wasSubmitted = this.setupSubmitted

      this.reset()
      this.workspaceId = resolvedWorkspaceId
      this.stepStatuses = stepStatuses
      this.setupSubmitted = wasSubmitted

      router.push('/onboarding/complete')
    },

    reset() {
      Object.assign(this, createInitialState())
    },
  },

  persist: {
    pick: [
      'company',
      'business',
      'cco',
      'auditor',
      'description',
      'stepStatuses',
      'workspaceId',
      'setupSubmitted',
      'exited',
      'masterAnswers',
    ],
  },
})
